import { Component, inject, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { Router } from "@angular/router"
import { firstValueFrom } from "rxjs"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { FormField, form, submit } from "@angular/forms/signals"
import { CreateSurvey, CreateSurveyFactory, SURVEY_FIELD_LABELS } from "../../models"
import { SurveyService, ToastService } from "../../services"
import { TableComponent } from "../../components/table/table.component"
import { FiltersComponent } from "../../components/filters/filters.component"
import { DateRangePickerComponent } from "../../components/date-range-picker/date-range-picker.component"
import { FiltersValue, emptyFilters, toDependencies } from "../../helpers/DependenciesForm"
import { invalidFieldsDetail, toServerFieldErrors } from "../../helpers/FormErrors"
import { BooleanInputComponent } from "../../components/boolean-input/boolean-input.component"
import { TextInputComponent } from "../../components/text-input/text-input.component"
import { FileInputComponent } from "../../components/file-input/file-input.component"
import {
    DictionaryInputComponent,
} from "../../components"

@Component({
    selector: "valben-surveys-add",
    templateUrl: "./surveys-add.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        NgClass,
        MatProgressSpinnerModule,
        FormField,
        FiltersComponent,
        DictionaryInputComponent,
        BooleanInputComponent,
        FileInputComponent,
        TextInputComponent,
        DateRangePickerComponent,
    ],
})
export class SurveysAddPage {
    private _surveyService = inject(SurveyService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    saving = signal(false)
    tomorrow = new Date(new Date().setHours(24, 0, 0, 0))

    model = signal<CreateSurvey>(CreateSurveyFactory.create())
    surveyForm = form(this.model)

    filters = signal<FiltersValue>(emptyFilters())

    save(): void {
        this._create(false)
    }

    draft(): void {
        this._create(true)
    }

    back(): void {
        this._router.navigate(["/surveys"])
    }

    private async _create(isDraft: boolean): Promise<void> {
        this.surveyForm.isDraft().value.set(isDraft)
        this.surveyForm.dependencies().value.set(toDependencies(this.filters()))
        this.saving.set(true)
        let failed = false

        const created = await submit(this.surveyForm, {
            action: async f => {
                try {
                    await firstValueFrom(this._surveyService.Create(f().value()))
                    return null
                } catch (err) {
                    failed = true
                    const { detail, errors } = toServerFieldErrors(err, this.surveyForm, SURVEY_FIELD_LABELS)
                    this._error(detail)
                    return errors
                }
            },
            onInvalid: () => this._error(invalidFieldsDetail(this.surveyForm, SURVEY_FIELD_LABELS)),
        })

        this.saving.set(false)
        if (failed || !created) return

        this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Uspješno uneseno",
            life: 10000,
        })
        TableComponent.resetPageOffset()
        this.back()
    }

    private _error(detail: string): void {
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }
}
